import React, { useState } from 'react';
import { useToast } from '../components/ToastContainer';
import useAuthStore from '../stores/useAuthStore';

const DiscussionThread = ({ thread, onVote, onReply, onResolve, isEditable = false }) => {
  const toast = useToast();
  const { user } = useAuthStore();
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAuthorInstructor = thread.course?.instructor && thread.course.instructor.toString() === thread.author?._id;

  const handleVote = async (targetId, voteType, targetType = 'thread') => {
    try {
      if (onVote) {
        await onVote(targetId, voteType, targetType);
        toast.success(`You ${voteType === 'upvote' ? 'upvoted' : 'downvoted'} successfully`);
      }
    } catch (err) {
      toast.error('Failed to record vote');
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return toast.error('Reply cannot be empty');

    try {
      setIsSubmitting(true);
      if (onReply) {
        await onReply(thread._id, replyContent.trim());
        toast.success('Reply posted successfully');
        setReplyContent('');
        setIsReplying(false);
      }
    } catch (err) {
      toast.error('Failed to post reply');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canResolve = isEditable || (user.role === 'admin') || (thread.course?.instructor?._id && thread.course.instructor._id.toString() === user.id);

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
      {/* Thread Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center">
          <img
            src={`${process.env.REACT_APP_SOCKET_URL}/public${thread.author?.avatar}`}
            alt={thread.author?.name}
            className="w-10 h-10 rounded-full mr-3"
          />
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium dark:text-neutral-300">{thread.author?.name}</h3>
              {isAuthorInstructor && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 text-xs font-medium rounded-full">
                  Instructor
                </span>
              )}
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-300">
              {new Date(thread.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-center space-y-2">
          <button
            onClick={() => handleVote(thread._id, 'upvote', 'thread')}
            className="p-1 text-neutral-400 hover:text-primary-500"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
          <span className="text-sm font-medium dark:text-neutral-300">
            {thread.upvotes.length - thread.downvotes.length}
          </span>
          <button
            onClick={() => handleVote(thread._id, 'downvote', 'thread')}
            className="p-1 text-neutral-400 hover:text-danger-500"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <h4 className="text-lg font-semibold mb-2 dark:text-neutral-300">{thread.title}</h4>
      <p className="text-neutral-600 dark:text-neutral-300 mb-4">{thread.content}</p>

      {/* Tags */}
      {thread.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {thread.tags.map((tag, i) => (
            <span key={i} className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-full">
              #{tag}
            </span>
          ))}
        </div>
      )}

      {thread.isResolved && (
        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 mb-4">
          Resolved
        </span>
      )}

      {/* Reply Button */}
      {!thread.isResolved && (
        <button
          onClick={() => setIsReplying(!isReplying)}
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline mb-4"
        >
          {isReplying ? 'Cancel Reply' : 'Reply'}
        </button>
      )
      }

      {isReplying && (
        <form onSubmit={handleReplySubmit} className="mb-4">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            placeholder="Write your reply..."
            className="w-full px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
            rows="3"
            required
          />
          <div className="mt-3 flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 text-white text-sm font-medium rounded-lg ${isSubmitting ? 'bg-gray-400' : 'bg-primary-500 hover:bg-primary-600'}`}
            >
              {isSubmitting ? 'Posting...' : 'Post Reply'}
            </button>
          </div>
        </form>
      )}

      {/* Replies */}
      {thread.replies?.length > 0 && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4 mt-4">
          <h5 className="text-sm font-medium mb-3 dark:text-neutral-400">
            {thread.replies.length} {thread.replies.length === 1 ? 'Reply' : 'Replies'}
          </h5>
          <div className="space-y-4">
            {thread.replies.map((reply, i) => {

              const isReplyInstructor = thread.course?.instructor &&
                thread?.course?.instructor.toString() === reply?.author?._id;

              return (
                <div key={i} className="bg-neutral-50 dark:bg-neutral-700/50 dark:text-neutral-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center">
                      <img
                        src={`${process.env.REACT_APP_SOCKET_URL}/public${reply.author?.avatar}`}
                        alt={reply.author?.name}
                        className="w-8 h-8 rounded-full mr-2"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium dark:text-neutral-300">{reply.author?.name}</p>
                          {isReplyInstructor && (
                            <span className="px-2 py-0.5 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 text-xs font-medium rounded-full">
                              Instructor
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-neutral-600 dark:text-neutral-300">
                          {new Date(reply.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-center space-y-1">
                      <button
                        onClick={() => handleVote(reply._id, 'upvote', 'reply')}
                        className="p-1 text-neutral-400 hover:text-primary-500"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="text-xs font-medium">
                        {reply.upvotes.length - reply.downvotes.length}
                      </span>
                      <button
                        onClick={() => handleVote(reply._id, 'downvote', 'reply')}
                        className="p-1 text-neutral-400 hover:text-danger-500"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">
                    {reply.content}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {canResolve && (
        <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          {thread.isResolved ? (
            <button
              onClick={() => onResolve(thread._id, false)}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded-lg"
            >
              Reopen Thread
            </button>
          ) : (
            <button
              onClick={() => onResolve(thread._id, true)}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg"
            >
              Mark as Resolved
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscussionThread;